import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Modal, Toast, Spinner } from 'react-bootstrap';
import { FaTrash, FaPlus, FaEdit, FaRedo } from 'react-icons/fa';
import { getNeighborhoodDevelopments, deleteNeighborhoodDevelopment, getNeighborhoodDevelopmentByGuid } from '../../services/neighborhoodService';
import NeighborhoodForm from './NeighborhoodForm';

const NeighborhoodList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [addModalVisible, setAddModalVisible] = useState(false);

  const [toastState, setToastState] = useState({
    show: false,
    message: '',
    variant: 'success'
  });

  const showToast = (message, variant = 'success') => {
    setToastState({ show: true, message, variant });
  };

  const hideToast = () => setToastState((prev) => ({ ...prev, show: false }));

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getNeighborhoodDevelopments();
      if (response?.isSuccess && Array.isArray(response.data)) {
        setData(response.data);
      } else {
        throw new Error(response?.resultMessage ?? 'Beklenmeyen bir sonuç alındı.');
      }
    } catch (err) {
      console.error('Error while fetching data:', err);
      setData([]);
      setError('Veriler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const confirmDelete = (item) => {
    setSelectedItem(item);
    setDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      const response = await deleteNeighborhoodDevelopment(selectedItem.guid);
      if (response?.isSuccess) {
        setData((prev) => prev.filter((item) => item.guid !== selectedItem.guid));
        showToast('Başarıyla silindi.');
      } else {
        showToast(response?.resultMessage ?? 'Silinirken bir hata oluştu.', 'danger');
      }
    } catch (err) {
      console.error('Delete error:', err);
      showToast('Silinirken bir hata oluştu.', 'danger');
    } finally {
      setDeleteModalVisible(false);
      setSelectedItem(null);
    }
  };

  const handleEdit = async (item) => {
    setEditModalVisible(true);
    setEditModalLoading(true);
    setEditingItem(null);
    try {
      const detailResponse = await getNeighborhoodDevelopmentByGuid(item.guid);
      if (detailResponse?.isSuccess && detailResponse.data) {
        setEditingItem(detailResponse.data);
      } else {
        throw new Error('Detaylar getirilemedi.');
      }
    } catch (err) {
      console.error('Error while loading details:', err);
      setEditModalVisible(false);
      showToast('Detaylar yüklenirken bir hata oluştu.', 'danger');
    } finally {
      setEditModalLoading(false);
    }
  };

  const handleAdd = () => {
    setAddModalVisible(true);
  };

  const handleEditSuccess = async () => {
    await fetchData();
    setEditModalVisible(false);
    setEditingItem(null);
    showToast('Başarıyla güncellendi.');
  };

  const handleAddSuccess = async () => {
    await fetchData();
    setAddModalVisible(false);
    showToast('Başarıyla eklendi.');
  };

  return (
    <Container fluid className="py-4">
      <Card className="mb-4">
        <Card.Body className="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
          <div>
            <h3 className="mb-2 mb-md-0">Mahallem Gelişiyor Yönetimi</h3>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Button variant="outline-secondary" onClick={fetchData} disabled={loading}>
              <FaRedo className="me-2" /> Yenile
            </Button>
            <Button variant="primary" onClick={handleAdd}>
              <FaPlus className="me-2" /> Yeni Ekle
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </Spinner>
            </div>
          ) : error ? (
            <div className="text-center py-5 text-danger fw-semibold">{error}</div>
          ) : data.length === 0 ? (
            <div className="text-center py-5 text-muted">Kayıt bulunamadı.</div>
          ) : (
            <Row className="g-4">
              {data.map((item) => {
                const getImageUrl = (url) => {
                  if (!url) return null;
                  if (url.startsWith('http')) return url;
                  
                  if (url.startsWith('/assets/')) return `https://iskenderun.bel.tr${url}`;
                  
                  if (url.startsWith('/uploads/')) {
                    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.iskenderun.bel.tr/api';
                    const apiDomain = apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl;
                    return `${apiDomain}${url}`;
                  }
                  
                  return `https://iskenderun.bel.tr${url.startsWith('/') ? url : '/' + url}`;
                };

                // Kapak fotoğrafı yoksa ilk kategori görselini kullan
                const resolveCoverImage = (mahalle) => {
                  if (mahalle.coverImage) return getImageUrl(mahalle.coverImage);

                  const sortedCategories = [...(mahalle.categories || [])].sort(
                    (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
                  );
                  for (const category of sortedCategories) {
                    if (Array.isArray(category.images) && category.images.length > 0) {
                      return getImageUrl(category.images[0].imageUrl);
                    }
                  }

                  if (Array.isArray(mahalle.images) && mahalle.images.length > 0) {
                    return getImageUrl(mahalle.images[0].imageUrl);
                  }

                  return null;
                };

                const coverSrc = resolveCoverImage(item);

                return (
                  <Col key={item.guid} xs={12} md={6} lg={4}>
                    <Card className="h-100 shadow-sm">
                      {coverSrc && (
                        <Card.Img
                          variant="top"
                          src={coverSrc}
                          alt={item.name}
                          style={{ height: 200, objectFit: 'cover' }}
                        />
                      )}
                      <Card.Body>
                        <Card.Title className="mb-3">{item.name}</Card.Title>
                        {item.title && <div className="fw-bold mb-2">{item.title}</div>}
                        {item.description && (
                          <Card.Text className="text-muted">
                            {item.description.length > 120
                              ? `${item.description.substring(0, 120)}…`
                              : item.description}
                          </Card.Text>
                        )}
                        <small className="text-muted d-block mt-2">Toplam Foto: {item.totalImages}</small>
                        <small className="text-muted d-block">Toplam Kategori: {item.categories?.length || 0}</small>
                      </Card.Body>
                    <Card.Footer className="bg-white">
                      <div className="d-flex gap-2">
                        <Button variant="outline-primary" size="sm" onClick={() => handleEdit(item)}>
                          <FaEdit className="me-1" /> Düzenle
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => confirmDelete(item)}>
                          <FaTrash className="me-1" /> Sil
                        </Button>
                      </div>
                    </Card.Footer>
                  </Card>
                </Col>
              );
            })}
            </Row>
          )}
        </Card.Body>
      </Card>

      <Modal show={deleteModalVisible} onHide={() => setDeleteModalVisible(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Silme Onayı</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu kaydı silmek istediğinizden emin misiniz? <br />
          <strong>{selectedItem?.name}</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteModalVisible(false)}>İptal</Button>
          <Button variant="danger" onClick={handleDelete}>Sil</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={editModalVisible} onHide={() => { setEditModalVisible(false); setEditingItem(null); }} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editModalLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <NeighborhoodForm editingItem={editingItem} onSubmit={handleEditSuccess} onCancel={() => setEditModalVisible(false)} />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={addModalVisible} onHide={() => setAddModalVisible(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Yeni Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NeighborhoodForm editingItem={null} onSubmit={handleAddSuccess} onCancel={() => setAddModalVisible(false)} />
        </Modal.Body>
      </Modal>

      <Toast
        show={toastState.show}
        onClose={hideToast}
        delay={3000}
        autohide
        bg={toastState.variant}
        style={{ position: 'fixed', top: 20, right: 20, zIndex: 1055 }}
      >
        <Toast.Body className="text-white">{toastState.message}</Toast.Body>
      </Toast>
    </Container>
  );
};

export default NeighborhoodList;
